import i18n from '~/i18n';

/**
 * Returns true if the value contains at least one Arabic character.
 * Arabic Unicode block: U+0600–U+06FF (char codes 1536–1791).
 *
 * Use this for detecting Arabic-language content in Thiqah-sourced fields.
 * It is intentionally separate from `isEnglish` to avoid side-effects on
 * existing validation logic elsewhere in the codebase.
 */
export const containsArabic = (value?: string | null): boolean => {
  if (!value) return false;

  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code >= 1536 && code <= 1791) {
      return true;
    }
  }

  return false;
};

/**
 * Returns true if the string contains at least one Arabic character.
 * Alias for `containsArabic` — use in form validators where inline
 * definitions were previously written.
 */
export const hasArabicChars = (text: string): boolean => /[\u0600-\u06FF]/.test(text);

/** Returns true if the string contains at least one English (Latin) character. */
export const hasEnglishChars = (text: string): boolean => /[a-zA-Z]/.test(text);

/**
 * Ant Design validator — rejects a value that contains Arabic characters.
 * Intended for fields that must be filled in English only.
 */
export function validateEnglishOnly(rule: any, value: string): Promise<void> {
  if (!value) return Promise.resolve();
  if (hasArabicChars(value))
    return Promise.reject(rule?.message ?? i18n.t('Please enter your company name.', { ns: 'ksaApplication' }));
  return Promise.resolve();
}

/**
 * Ant Design validator — rejects a value that contains English characters.
 * Intended for fields that must be filled in Arabic only.
 */
export function validateArabicOnly(rule: any, value: string): Promise<void> {
  if (!value) return Promise.resolve();
  if (hasEnglishChars(value))
    return Promise.reject(rule?.message ?? i18n.t('Please enter your company name.', { ns: 'ksaApplication' }));
  return Promise.resolve();
}
